import { twMerge } from 'tailwind-merge';

import { usePostViewer } from '../../helpers/hooks/usePostViewer';
import {
  useIsMobileScreenWidthOrThinner,
  useIsTabletScreenWidthOrThinner,
} from '../../helpers/hooks/useScreenWidth';
import { Heading } from '../Heading/Heading';
import { MAIN_NAVIGATION_MIN_HEIGHT } from '../Navigation/MainNavigation/constants/constants';
import { PostsViewer } from '../PostsViewer/PostsViewer';
import { openPostViewerEventSources } from '../PostsViewer/types/openPostViewerEventSources';

import { ReactNode, useState } from 'react';
import { FishingReportCTASection } from './components/FishingReportCTASection';
import { FishingReportCard, PostExtended } from './components/FishingReportCard';
import {
  FISHINGREPORT_SECTION_MAX_CATCHES_DESKTOP,
  FISHINGREPORT_SECTION_MAX_CATCHES_MOBILE,
  FISHINGREPORT_SECTION_MAX_CATCHES_TABLET,
} from './constants/constants';
import { Pill } from '../Clickables/Pill/Pill';
import { AppDownloadModal } from '../AppDownloadModal/AppDownloadModal';
import appdownloadPhone from './assets/AppdownloadPhone.png';
import appdownloadPhoneMobile from './assets/AppdownloadPhoneMobile.png';
interface IProps {
  className?: string;
  id: string;
  fakeFilters?: string[];
  recentCatchPostList: PostExtended[];
  postsReference: React.RefObject<HTMLDivElement>;
  postViewerShowMoreTeaser?: ReactNode;
  showFishingWaterName?: boolean;
  sourceId?: string;
  title: string;
}

export const FishingReportsGrid = ({
  className,
  id = 'fishing-reports-grid',
  fakeFilters,
  recentCatchPostList,
  postsReference,
  postViewerShowMoreTeaser,
  showFishingWaterName = false,
  sourceId,
  title = 'Fishing reports',
}: IProps) => {
  const {
    currentPostViewerId,
    closePostViewer,
    isPostViewerOpen,
    openPostViewer,
    setCurrentPostViewerId,
  } = usePostViewer(postsReference, MAIN_NAVIGATION_MIN_HEIGHT + 4);
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const [activeFakeFilter, setActiveFakeFilter] = useState<number | undefined>();

  const isTabletScreenWidthOrThinner = useIsTabletScreenWidthOrThinner();
  const isMobileScreenWidthOrThinner = useIsMobileScreenWidthOrThinner();

  const postViewerMaxCatchCount = isMobileScreenWidthOrThinner
    ? FISHINGREPORT_SECTION_MAX_CATCHES_MOBILE
    : isTabletScreenWidthOrThinner
      ? FISHINGREPORT_SECTION_MAX_CATCHES_TABLET
      : FISHINGREPORT_SECTION_MAX_CATCHES_DESKTOP;

  const hasFakeFilters = fakeFilters && fakeFilters.length > 0;

  return (
    <div
      id={id}
      className={twMerge(
        'w-full scroll-mt-[--navigation-scrolltop-offset-mobile] sm:scroll-mt-[--navigation-scrolltop-offset]',
        className,
      )}
    >
      <Heading className={hasFakeFilters ? 'mb-4' : 'mb-6 sm:mb-10'} variant="sm" as="h2">
        {title}
      </Heading>
      {hasFakeFilters && (
        <div className="mb-4 flex flex-row flex-nowrap gap-2 overflow-auto whitespace-nowrap">
          {fakeFilters.map((filter, index) => (
            <Pill
              variant="whiteBg"
              size="sm"
              isActive={activeFakeFilter === index}
              key={filter}
              onClick={() => {
                setActiveFakeFilter(index);
                setIsAppDownloadModalOpen(true);
              }}
            >
              {filter}
            </Pill>
          ))}
        </div>
      )}
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-12 sm:gap-4">
        {recentCatchPostList.map((post, index) => (
          <FishingReportCard
            key={post._id}
            post={post}
            className={`col-span-2 sm:col-span-4 md:col-span-3 ${index >= FISHINGREPORT_SECTION_MAX_CATCHES_MOBILE ? 'hidden' : ''} ${index >= FISHINGREPORT_SECTION_MAX_CATCHES_TABLET ? 'sm:hidden' : ''} ${index >= FISHINGREPORT_SECTION_MAX_CATCHES_DESKTOP ? 'md:hidden' : ''}`}
            openPostViewer={openPostViewer}
            openPostViewerEventSource={openPostViewerEventSources.FishingReportRecentCatch}
            showFishingWaterName={showFishingWaterName}
          />
        ))}
      </div>
      <FishingReportCTASection catchCount={recentCatchPostList.length} />
      {isPostViewerOpen && currentPostViewerId && (
        <PostsViewer
          posts={recentCatchPostList.slice(0, postViewerMaxCatchCount)}
          currentPostViewerId={currentPostViewerId}
          hasMore={false}
          isPostViewerOpen
          loadMore={(): void => {}}
          onClosePostsViewer={closePostViewer}
          setCurrentPostViewerId={setCurrentPostViewerId}
          showMoreTeaser={postViewerShowMoreTeaser}
        />
      )}
      {hasFakeFilters && (
        <AppDownloadModal
          appDownloadSource="FishingReportsGrid"
          appDownloadSourceId={sourceId}
          closeButtonVariant={isMobileScreenWidthOrThinner ? 'textNone' : 'controlWhiteBg'}
          modalHeader={'Find more fish, faster – Use catch filters in the Fishbrain app'}
          modalHeaderClassName="sm:mr-16 md:mr-16"
          modalHeaderVariant="sm"
          modalText=""
          isModalOpen={isAppDownloadModalOpen}
          image={appdownloadPhone}
          imageMobile={appdownloadPhoneMobile}
          imageWrapperClassName="bg-tuna-tint px-8 sm:p-6 md:mr-4"
          onCloseModal={() => {
            setIsAppDownloadModalOpen(false);
            setActiveFakeFilter(undefined);
          }}
          textWrapperClassName="mt-6 sm:pb-0 md:pr-4 md:pb-4"
          showQRCodeHint
          width="700px"
        />
      )}
    </div>
  );
};
